import { Injectable } from '@angular/core';
import type { MAEWStatus, TimeSlot } from '../models/job.model';

/**
 * Result of classifying an attempt's date/time into a MAEW slot.
 *
 * `primary` is the time-of-day letter (M/A/E); `isWeekend` is `true` on
 * Saturday or Sunday. The two together select **exactly one** credited slot —
 * see {@link creditedSlotKey}.
 */
export interface SlotClassification {
  /** Time-of-day slot: `'M'` 00:00–11:59, `'A'` 12:00–16:59, `'E'` 17:00–23:59. */
  primary: 'M' | 'A' | 'E';
  /** `true` on Saturday (day=6) or Sunday (day=0). */
  isWeekend: boolean;
}

/** The four MAEW slot keys, in display order. */
const MAEW_KEYS = ['M', 'A', 'E', 'W'] as const;

/**
 * Maps a {@link MAEWStatus} key to its {@link TimeSlot} label.
 *
 * @remarks
 * The inverse of the `maewKey` map in `JobsService.recordAttempt()`, which
 * takes a `TimeSlot` and sets the matching flag.
 */
export const SLOT_LABEL: Record<keyof MAEWStatus, TimeSlot> = {
  M: 'Morning',
  A: 'Afternoon',
  E: 'Evening',
  W: 'Weekend',
};

/**
 * The single MAEW slot an attempt in `slot` credits.
 *
 * @remarks
 * **One attempt credits exactly one slot** — a weekend attempt credits `'W'`
 * and *never* also its time-of-day letter. This mirrors the server, which is
 * authoritative: its `timeofday`-generation emits `Wm`/`Wa`/`We` on a weekend
 * day and never the bare letter, and the bare `M`/`A`/`E` on a weekday and
 * never a `Wx` token — mutually exclusive per attempt. `mapMaewFromAttempts()`
 * (`job.adapter.ts`) already decodes it that way; this function is what keeps
 * the client's own optimistic crediting and its final-attempt prediction
 * consistent with it.
 *
 * Returning a single key (rather than a set) is deliberate: it makes the old
 * double-credit bug — where a Saturday-Afternoon attempt set both `A` and `W`,
 * letting 3 attempts cover 4 slots — structurally impossible to reintroduce.
 *
 * **This is slot *selection*, not eligibility.** Nothing here gates *whether*
 * a final attempt may occur; per MOB-298 the final attempt is simply the 4th
 * entry covering the 4th slot, in any order, on any day.
 *
 * @param slot - Classification of the attempt's date/time.
 * @returns The one {@link MAEWStatus} key this attempt credits.
 */
export function creditedSlotKey(slot: SlotClassification): keyof MAEWStatus {
  return slot.isWeekend ? 'W' : slot.primary;
}

/**
 * `true` when logging an attempt in `slot` would leave all four MAEW slots
 * covered — i.e. this is the Final Attempt.
 *
 * @remarks
 * The MOB-298 rule: the four attempts must span M, A, E and W, **in any
 * order and regardless of which day the closing attempt falls on**. There is
 * deliberately no weekend gate here (an earlier revision required the closing
 * attempt to be a Weekend one; that was removed).
 *
 * Since the current attempt contributes exactly one slot
 * ({@link creditedSlotKey}), reaching all four genuinely requires four
 * separate entries.
 *
 * @param maew - The job's MAEW flags **before** this attempt is recorded.
 * @param slot - Classification of the attempt's date/time.
 * @returns `true` when every slot is either already flagged or credited now.
 */
export function wouldExhaustMaew(maew: MAEWStatus, slot: SlotClassification): boolean {
  const credited = creditedSlotKey(slot);
  return MAEW_KEYS.every(key => maew[key] || key === credited);
}

/**
 * Derives the MAEW slot from the device's local time.
 *
 * @remarks
 * Slot rules match the prototype (`hr < 12 ? 'Morning' : hr < 17 ? 'Afternoon' : 'Evening'`):
 * - **M** — 00:00–11:59 (Morning, including overnight/early hours)
 * - **A** — 12:00–16:59 (Afternoon)
 * - **E** — 17:00–23:59 (Evening)
 *
 * `isWeekend` is `true` on Saturday/Sunday. It selects `'W'` as the attempt's
 * *one* credited slot in place of `primary` — see {@link creditedSlotKey}; it
 * is **not** a dual-credit, and it does not gate final-attempt eligibility.
 */
@Injectable({ providedIn: 'root' })
export class MaewService {
  /**
   * Derives the MAEW slot classification from `now`.
   *
   * @param now - The current time (defaults to `new Date()` — injectable for tests).
   * @returns A {@link SlotClassification} with `primary` and `isWeekend`.
   */
  classifySlot(now = new Date()): SlotClassification {
    const hour      = now.getHours();
    const day       = now.getDay();
    const isWeekend = day === 0 || day === 6;
    const primary   = hour < 12 ? 'M'
                    : hour < 17 ? 'A'
                    :             'E';
    return { primary, isWeekend };
  }
}
